import type { ContactGetOneAPI } from '@hero/api';
import { getObjectId } from '@hero/schema';
import { useApi } from '@hero/context';
import type {
  UseContactApi,
  UseContactCreateApi,
  UseContactDeleteApi,
  UseContactListApi,
  UseContactUpdateApi,
  UseContactsIdsApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';
import { ObjectId } from 'bson';

/**
 * Get single contact by ID. Pass id for normal use; pass undefined to get runQuery only (e.g. in selectors).
 *
 * Hook API: {@link UseContactApi}
 */
export const useContactApi: UseContactApi = (id, apiConfig) => {
  const api = useApi();
  const _id = id ? getObjectId(id) : undefined;
  return useQueryAPI({
    entity: 'contact',
    apiFn: api.contacts.getContactById,
    query: { _id: _id as ObjectId },
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * List contacts with pagination (skip/limit). Query may include customerId, isOwnContact.
 * API from ApiProvider; pass apiConfig to override apiFn, limit, etc.
 *
 * Hook API: {@link UseContactListApi}
 */
export const useContactListApi: UseContactListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'contact',
    apiFn: api.contacts.getContacts,
    query,
    apiConfig,
    isEnabled: () => true,
  });
};

/**
 * Create contact. Handles cache invalidation and optional onSuccess/onError.
 * API from ApiProvider; pass apiConfig to override apiFn, invalidateQueryKeys, invalidationStrategy, etc.
 *
 * Hook API: {@link UseContactCreateApi}
 */
export const useContactCreateApi: UseContactCreateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'contact',
    apiFn: api.contacts.createContact,
    apiConfig,
  });
};
export type UseContactCreateApiBody = Parameters<ReturnType<UseContactCreateApi>['mutate']>[0];

/**
 * Update contact. Handles cache invalidation and optional onSuccess/onError.
 * API from ApiProvider; pass apiConfig to override apiFn, etc.
 *
 * Hook API: {@link UseContactUpdateApi}
 */
export const useContactUpdateApi: UseContactUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'contact',
    apiFn: api.contacts.updateContact,
    apiConfig,
  });
};

/**
 * Delete contact. Handles cache invalidation and optional onSuccess/onError.
 *
 * Hook API: {@link UseContactDeleteApi}
 */
export const useContactDeleteApi: UseContactDeleteApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'contact',
    apiFn: api.contacts.deleteContact,
    apiConfig,
  });
};
